/**
 * Turning a pasted video link into the embed URL the sanitizer will accept.
 *
 * Lives here rather than beside the Tiptap node that uses it because it is the
 * client half of a two-sided contract: whatever this returns has to satisfy
 * EMBED_URL_PATTERNS in src/lib/sanitize-html.ts (and its mirror in
 * functions/src/lib/sanitize-html.ts), or the embed is stripped on save and the
 * seller watches their video disappear a second after they added it. Keeping it
 * out of the editor bundle also keeps it importable by the tests, which is the
 * only place that contract can actually be checked.
 */

/** A YouTube video id, as it appears in every URL shape below. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{6,20}$/;

/**
 * Normalise a pasted URL to a supported embed URL, or null.
 *
 * Accepts the forms people actually paste — a watch URL, a youtu.be short link,
 * a /shorts/ or /live/ link, an /embed/ URL, a vimeo.com/<id> page — and
 * rewrites every YouTube one to the NOCOOKIE host. That rewrite is the point,
 * not a detail: youtube.com sets its tracking cookies as soon as the frame
 * loads, before the visitor has pressed anything, which would make every
 * product page carrying a video a page that needs a consent gate it does not
 * have.
 */
export function toEmbedUrl(raw: string): string | null {
  const input = (raw || '').trim();
  if (!input) return null;

  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return null;
  }
  // http is accepted on the way IN (people paste it) but never on the way out:
  // every return below is an https literal.
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  const host = url.hostname.toLowerCase().replace(/^www\./, '');

  if (host === 'youtube.com' || host === 'youtube-nocookie.com'
      || host === 'music.youtube.com' || host === 'm.youtube.com') {
    const fromQuery = url.searchParams.get('v');
    if (fromQuery && YOUTUBE_ID.test(fromQuery)) {
      return `https://www.youtube-nocookie.com/embed/${fromQuery}`;
    }
    // /embed/<id>, /shorts/<id>, /v/<id> and /live/<id> all carry the id in the
    // same position.
    const m = url.pathname.match(/^\/(?:embed|shorts|v|live)\/([A-Za-z0-9_-]{6,20})$/);
    return m ? `https://www.youtube-nocookie.com/embed/${m[1]}` : null;
  }

  if (host === 'youtu.be') {
    const id = url.pathname.slice(1);
    return YOUTUBE_ID.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    // CONTAINER URLs are rejected rather than guessed at. A showcase, album,
    // channel-by-id or event link carries a numeric id in exactly the same
    // position a video link does, so the loose "first run of digits" match
    // below happily read a SHOWCASE id as a video id and built a player URL
    // for a video that does not exist. The seller pasted a real Vimeo link,
    // the editor accepted it, the sanitizer accepted it (the shape is valid),
    // and the buyer got an error frame on the product page. There is no way to
    // resolve a container to a video without Vimeo's API, so the honest
    // outcome is to say the link is not supported and let the seller paste the
    // individual video instead.
    //
    // `/album/<id>/video/<realId>` is the one container form that DOES name a
    // video, so it is picked out by its own pattern rather than being refused.
    // A CHANNEL or GROUP link ends on the video's own id, so those stay
    // supported too; every other container root is refused.
    const path = url.pathname;
    const albumVideo = path.match(/^\/album\/\d{6,15}\/video\/(\d{6,15})$/);
    const isContainer = /^\/(?:showcase|album|event|folder|ondemand)\//.test(path)
      || (/^\/(?:channels|groups)\//.test(path)
          && !/^\/(?:channels|groups)\/[^/]+\/(?:videos\/)?\d{6,15}$/.test(path));

    // Resolved in one place so the hash handling below applies to every form,
    // including the album one. An early return there would have dropped the
    // unlisted hash off exactly the links most likely to carry one.
    const videoId = albumVideo
      ? albumVideo[1]
      : isContainer
        ? null
        : (path.match(/(?:^|\/)(\d{6,15})(?:$|\/)/)?.[1] ?? null);
    if (!videoId) return null;
    // The UNLISTED-VIDEO HASH has to survive. Vimeo shares a private video as
    // vimeo.com/<id>/<hash> (or player.vimeo.com/video/<id>?h=<hash>), and the
    // player refuses to load without it — so dropping the query, as the obvious
    // implementation does, turns "paste your Vimeo link" into a black box for
    // exactly the sellers most likely to be embedding a preview of paid work.
    //
    // Taken from `?h=` when present, otherwise from the path segment after the
    // id, and matched narrowly so nothing else from the query is carried into a
    // URL the sanitizer then has to accept.
    const fromQuery = url.searchParams.get('h');
    // ANCHORED to the whole path. Unanchored it scanned anywhere, so a nested
    // form like /album/<id>/video/<id> could have a later segment read as this
    // video's private hash. Only the two shapes that actually carry a hash in
    // the path are matched: vimeo.com/<id>/<hash> and its /video/ variant.
    const fromPath = path.match(/^\/(?:video\/)?\d{6,15}\/([A-Za-z0-9]{6,20})\/?$/);
    const hash = (fromQuery && /^[A-Za-z0-9]{6,20}$/.test(fromQuery))
      ? fromQuery
      : (fromPath ? fromPath[1] : null);
    return hash
      ? `https://player.vimeo.com/video/${videoId}?h=${hash}`
      : `https://player.vimeo.com/video/${videoId}`;
  }

  return null;
}
/**
 * The poster image for a video link, or null when there isn't one.
 *
 * YouTube derives it from the video id, so no API call is needed. Vimeo does
 * not: its thumbnails live behind an oEmbed lookup keyed on the video, which
 * would mean a server round trip per thumbnail and a second thing to cache and
 * invalidate, so a Vimeo item keeps the play glyph instead.
 *
 * `mqdefault` rather than `hqdefault`: hqdefault is a 4:3 canvas with black
 * bars baked into a widescreen video's poster, and every place this is shown is
 * a square that crops the sides, so the bars would survive and the picture
 * would not. mqdefault is a clean 16:9 and exists for every video, which
 * maxresdefault does not.
 *
 * Returned as a SAME-ORIGIN /api/image-proxy URL, so the file is fetched by our
 * server and served from our own origin. The visitor's browser never asks
 * Google for it, which is the same reason the player itself is pinned to the
 * nocookie host. This used to return the i.ytimg.com URL itself on the theory
 * that next/image would proxy it, and in production nothing did: App Hosting
 * builds with images.unoptimized, so next/image rendered a plain <img> pointing
 * at i.ytimg.com, the production CSP's img-src (which names no Google image
 * host) blocked it, and every video fell back to the play glyph.
 *
 * Null for an id youTubePosterUpstream would refuse (a real YouTube id is 11
 * characters; toEmbedUrl accepts from 6), since the proxy would only answer 403.
 */
export function videoThumbnailUrl(raw: string): string | null {
  const embed = toEmbedUrl(raw);
  if (!embed) return null;
  const youtube = embed.match(/^https:\/\/www\.youtube-nocookie\.com\/embed\/([A-Za-z0-9_-]{6,20})$/);
  if (!youtube) return null;
  const upstream = youTubePosterUpstream(`https://i.ytimg.com/vi/${youtube[1]}/mqdefault.jpg`);
  return upstream ? `/api/image-proxy?url=${encodeURIComponent(upstream)}` : null;
}

/**
 * The ONE YouTube URL shape /api/image-proxy agrees to fetch: a video's
 * mqdefault poster on i.ytimg.com, exactly as videoThumbnailUrl builds it.
 * Returns that URL when `raw` is it, character for character, and null for
 * anything else.
 *
 * The proxy is unauthenticated by necessity, so what it will fetch is its only
 * real control (see src/lib/storage-url.ts for the Storage half). Matched on
 * the raw string with an anchored pattern rather than on a parsed URL's parts,
 * so there is nothing for a parser to normalise: another host or a look-alike
 * (i.ytimg.com.evil.example, i.ytimg.com@evil.example), a port, a query string
 * or fragment (even an empty "?"), percent-escapes, dot segments and any other
 * file on the host all fail the one pattern. The id is 11 to 20 characters of
 * YouTube's id alphabet; real ids are 11.
 */
export function youTubePosterUpstream(raw: string): string | null {
  const m = /^https:\/\/i\.ytimg\.com\/vi\/([A-Za-z0-9_-]{11,20})\/mqdefault\.jpg$/.exec(raw);
  return m ? `https://i.ytimg.com/vi/${m[1]}/mqdefault.jpg` : null;
}

/**
 * The provider a video link belongs to, for labelling an item with no poster.
 *
 * Matched on the HOST rather than by searching the whole URL for the provider's
 * name. A YouTube id is 6 to 20 characters of `[A-Za-z0-9_-]`, so an id can
 * contain the literal string `vimeo` and a substring test reads that video as
 * a Vimeo one. Both return values of `toEmbedUrl` are https literals with a
 * fixed host, so a prefix test is exact.
 */
export function videoProviderName(raw: string): 'YouTube' | 'Vimeo' | null {
  const embed = toEmbedUrl(raw);
  if (!embed) return null;
  return embed.startsWith('https://player.vimeo.com/') ? 'Vimeo' : 'YouTube';
}
